import React, { useCallback, useEffect } from 'react';
import { useProducts } from './useProducts';
import { HomeCatalogShelves, HomeFeaturedShelf } from './HomeDynamicCatalog';
import HomeNextExhibitionBanner from './HomeNextExhibitionBanner';
import { useExhibitions } from './useExhibitions';
import { useSiteSettings } from './SiteSettingsContext';
import PageSeoHelmet from './PageSeoHelmet';
import StorefrontHero from './components/StorefrontHero';
import HomeInstagramSection from './components/home/HomeInstagramSection';
import RecentlyViewedStrip from './recentlyViewed/RecentlyViewedStrip';
import { usePullToRefresh } from './usePullToRefresh';
import HomeAssistantCard from './components/home/HomeAssistantCard';
import HomeTrustStrip from './components/home/HomeTrustStrip';
import HomeMerchandisingTiles from './components/home/HomeMerchandisingTiles';
import HomeProductShelves from './components/home/HomeProductShelves';
import HomeTestimonials from './components/home/HomeTestimonials';

const Home = () => {
  const { allProducts, refetch: refetchCatalog } = useProducts();
  const exhibitions = useExhibitions();
  const { settings: site, refresh: refreshSettings } = useSiteSettings();

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      refetchCatalog({ silent: true }),
      refreshSettings(),
      exhibitions.refetch(),
    ]);
  }, [exhibitions.refetch, refetchCatalog, refreshSettings]);

  const { pulling, ready, refreshing, pullDistance, threshold } = usePullToRefresh(handleRefresh, {
    threshold: 110,
  });

  const homeCopy = site.page_copy?.home || {};
  const showExhibition = exhibitions.status === 'ready' && exhibitions.upcoming.length > 0;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Wine band fills header clearance — avoids cream gap when announcement bar is off */
  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return undefined;
    main.classList.add('main-content--home-luxury');
    return () => main.classList.remove('main-content--home-luxury');
  }, []);

  return (
    <div className="Home-main Home-main--luxury">
      <PageSeoHelmet page="home" />

      {(pulling || refreshing) ? (
        <div
          className={`home-pull-refresh${ready ? ' home-pull-refresh--ready' : ''}${refreshing ? ' home-pull-refresh--active' : ''}`}
          aria-live="polite"
          aria-hidden={!pulling && !refreshing}
          style={{ '--pull-progress': `${Math.min(100, (pullDistance / threshold) * 100)}%` }}
        >
          <span className="home-pull-refresh__label">
            {refreshing ? 'Refreshing…' : ready ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      ) : null}

      <div
        className={`home-luxury-band home-luxury-band--dynamic${
          showExhibition ? ' home-luxury-band--has-exhibition' : ' home-luxury-band--no-exhibition'
        }`}
      >
        {showExhibition ? (
          <div className="home-top-strips home-top-strips--luxury">
            <HomeNextExhibitionBanner upcoming={exhibitions.upcoming} homeCopy={homeCopy} />
          </div>
        ) : null}

        <StorefrontHero />
      </div>

      <HomeTrustStrip />

      <HomeMerchandisingTiles />

      <HomeFeaturedShelf />

      <HomeProductShelves />

      <HomeCatalogShelves />

      <HomeAssistantCard site={site} />

      <HomeTestimonials />

      <RecentlyViewedStrip className="recently-viewed home-recently-viewed" />

      <HomeInstagramSection site={site} homeCopy={homeCopy} allProducts={allProducts} />
    </div>
  );
};

export default Home;
